import { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import type { SettingProfileEnum } from "graphql_globals";
import { useMutation } from "util/graphql";
import { pushNotification } from "common/core/notification_center/actions";
import { NOTIFICATION_SUBTYPES, NOTIFICATION_TYPES } from "constants/notifications";
import WorkflowModal from "common/modals/workflow_modal";
import { captureException } from "util/exception";
import Button from "common/core/button";
import AlertMessage from "common/core/alert_message";

import Styles from "./index.module.scss";
import SetSettingProfileDefaultMutation from "./graphql/set_setting_profile_default_mutation.graphql";

const MESSAGES = defineMessages({
  toggleOnTitle: {
    id: "276c366a-bd49-4132-912d-1e01d7380d8f",
    defaultMessage: "Set default {profileType} profile",
  },
  toggleOffTitle: {
    id: "238906ad-55b4-4bb3-b511-b8a81bcd8b9a",
    defaultMessage: "Remove default {profileType} profile",
  },
  newDefaultType: {
    id: "e408cee7-df24-420d-96a2-f83fb132fe16",
    defaultMessage: "New default {profileType}",
  },
  currentDefaultType: {
    id: "26f81b69-9716-4d30-a3fe-adf54e148488",
    defaultMessage: "Current default {profileType}",
  },
  toggleOnProfileDesc: {
    id: "539d7dbb-42f6-447b-b58d-bac9c97ca7d1",
    defaultMessage:
      "Your new default {profileType} will be applied to all newly created child organizations, but will not change any existing organizations’ {profileType}s. ",
  },
  toggleOffProfileDesc: {
    id: "bb37d7f8-5926-452c-afb9-85ab72211a10",
    defaultMessage:
      "By continuing, new organizations will have no default {profileType} profile upon creation.",
  },
  cancel: {
    id: "d3d3f26c-2988-40aa-a90d-0be6da5a61ef",
    defaultMessage: "Cancel",
  },
  saveChanges: {
    id: "82a97fe1-b44a-458c-a401-50bc75b4506d",
    defaultMessage: "Save changes",
  },
  toggleOnMutationSubmitError: {
    id: "a4bc7a1a-2f01-423a-bf16-77ba5761ca84",
    defaultMessage:
      "There was an error setting {profileName} as the default {profileType} profile. Please try again or contact support",
  },
  toggleOffMutationSubmitError: {
    id: "8c4a3ad9-31dc-4708-9a16-40dbf934a317",
    defaultMessage:
      "There was an error removing {profileName} as the default {profileType} profile. Please try again or contact support",
  },
  toggleOnSuccessMSg: {
    id: "cdda125a-0320-413d-a3d6-7c7b77ac03ba",
    defaultMessage: "{profileName} set as default profile for new organizations.",
  },
  toggleOffSuccessMSg: {
    id: "cdda125a-0320-413d-a3d6-7c7b77ac03ba",
    defaultMessage: "{profileName} removed as default profile.",
  },
  noProfile: {
    id: "8726422e-c3ed-478d-a791-3c5f4f530b0e",
    defaultMessage: "No profile",
  },
});

export type SetDefaultProfileModalPropTypes = {
  profileToToggle: {
    id: string;
    profileName: string;
    isDefault: boolean;
  };
  currentProfile?: {
    id: string;
    profileName: string;
  } | null;
  profileTypeEnum: SettingProfileEnum;
  onModalClose: () => void;
};

export function ToggleDefaultProfileModal({
  profileToToggle,
  currentProfile,
  profileTypeEnum,
  onModalClose,
}: SetDefaultProfileModalPropTypes) {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const profileType = profileTypeEnum.toLowerCase();
  const newIsDefaultValue = !profileToToggle.isDefault;

  const upsertSettingProfile = useMutation(SetSettingProfileDefaultMutation);

  const onSave = () => {
    setIsLoading(true);
    setShowError(false);
    upsertSettingProfile({
      variables: {
        input: {
          profileId: Number(profileToToggle.id),
          isDefault: newIsDefaultValue,
        },
      },
      update(cache) {
        cache.modify({
          id: cache.identify(profileToToggle),
          fields: {
            isDefault() {
              return newIsDefaultValue;
            },
          },
        });
        if (currentProfile && newIsDefaultValue) {
          cache.modify({
            id: cache.identify(currentProfile),
            fields: {
              isDefault() {
                return false;
              },
            },
          });
        }
      },
    })
      .then(() => {
        const message = newIsDefaultValue
          ? MESSAGES.toggleOnSuccessMSg
          : MESSAGES.toggleOffSuccessMSg;

        pushNotification({
          type: NOTIFICATION_TYPES.DEFAULT,
          subtype: NOTIFICATION_SUBTYPES.SUCCESS,
          message: intl.formatMessage(message, {
            profileName: profileToToggle.profileName,
          }),
          position: "topCenter",
        });
        onModalClose();
      })
      .catch((e) => {
        setShowError(true);
        captureException(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <WorkflowModal
      autoFocus
      title={intl.formatMessage(
        newIsDefaultValue ? MESSAGES.toggleOnTitle : MESSAGES.toggleOffTitle,
        {
          profileType,
        },
      )}
      buttons={[
        <Button
          key="cancel"
          buttonColor="dark"
          variant="tertiary"
          onClick={onModalClose}
          isLoading={isLoading}
        >
          {intl.formatMessage(MESSAGES.cancel)}
        </Button>,
        <Button
          key="save"
          buttonColor="action"
          variant="primary"
          onClick={onSave}
          isLoading={isLoading}
          automationId="save-button"
        >
          {intl.formatMessage(MESSAGES.saveChanges)}
        </Button>,
      ]}
      footerSeparator={false}
      footerText={
        showError && (
          <AlertMessage kind="danger">
            {intl.formatMessage(
              newIsDefaultValue
                ? MESSAGES.toggleOnMutationSubmitError
                : MESSAGES.toggleOffMutationSubmitError,
              {
                profileName: profileToToggle.profileName,
                profileType,
              },
            )}
          </AlertMessage>
        )
      }
    >
      {currentProfile && (
        <>
          <span>
            {intl.formatMessage(MESSAGES.currentDefaultType, {
              profileType,
            })}
          </span>

          <ul className={Styles.bulletList}>
            <li key={"new-default"}>{currentProfile.profileName}</li>
          </ul>
        </>
      )}

      <span>
        {intl.formatMessage(MESSAGES.newDefaultType, {
          profileType,
        })}
      </span>

      <ul className={Styles.bulletList}>
        {newIsDefaultValue ? (
          <li key={"new-default"}>{profileToToggle.profileName}</li>
        ) : (
          <li key={"new-default"}>{intl.formatMessage(MESSAGES.noProfile)}</li>
        )}
      </ul>

      <p>
        {intl.formatMessage(
          newIsDefaultValue ? MESSAGES.toggleOnProfileDesc : MESSAGES.toggleOffProfileDesc,
          { profileType },
        )}
      </p>
    </WorkflowModal>
  );
}
